import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { resolveAdminMediaUrl } from '@formosa/shared/utils/mediaUrl';
import {
  DEFAULT_LANG,
  mergeCustomerImageConfigWithLanguages,
  stripCustomerImageConfigs,
} from './customerCenterManagementConstants';

export default function CustomerImageConfigModal({ open, onClose, onSaved }) {
  const [configs, setConfigs] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingKey, setUploadingKey] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const [langList, imageConfigs] = await Promise.all([
          adminApi.newsLanguages(),
          adminApi.findCustomerImageConfig(),
        ]);
        if (cancelled) {
          return;
        }
        setLanguages(langList || []);
        setConfigs(mergeCustomerImageConfigWithLanguages(langList, imageConfigs));
      } catch (err) {
        if (!cancelled) {
          setError(err.message || '載入失敗');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [open]);

  async function handleUpload(index, event) {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const key = configs[index]?.lang || `idx-${index}`;
    setUploadingKey(key);
    setError('');
    try {
      const url = await adminApi.uploadOssImage(file);
      setConfigs((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], imageUrl: url };
        return next;
      });
    } catch (err) {
      setError(err.message || '圖片上傳失敗');
    } finally {
      setUploadingKey('');
      event.target.value = '';
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    const defaultConfig = configs.find((item) => item.lang === DEFAULT_LANG);
    if (!defaultConfig?.imageUrl?.trim()) {
      setError('請上傳預設客服中心圖片');
      return;
    }
    setSubmitting(true);
    try {
      await adminApi.updateCustomerImageConfig(stripCustomerImageConfigs(configs));
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.message || '保存失敗');
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) {
    return null;
  }

  return (
    <div className="confirm-dialog-overlay" role="presentation" onClick={onClose}>
      <div
        className="modal-panel modal-panel--wide"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-panel__header">
          <h3>客服中心圖片配置</h3>
          <button type="button" className="btn btn-link" onClick={onClose}>
            ×
          </button>
        </div>

        <form className="modal-panel__body static-content-edit-form" onSubmit={handleSubmit}>
          {error ? <p className="form-message form-message--error">{error}</p> : null}
          {loading ? <p className="table-empty">載入中…</p> : null}

          {!loading
            ? configs.map((config, index) => (
                <div key={config.lang} className="form-field">
                  <label>
                    客服中心圖片（{config._label || config.lang}）
                    {config._required ? ' *' : ''}
                  </label>
                  <div className="carousel-image-upload">
                    {config.imageUrl ? (
                      <img src={resolveAdminMediaUrl(config.imageUrl)} alt="" className="carousel-thumb-preview" />
                    ) : (
                      <div className="carousel-thumb-placeholder">未設定</div>
                    )}
                    <label className="btn btn-success">
                      {uploadingKey === config.lang ? '上傳中…' : '上傳客服圖片（343×194）'}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        disabled={Boolean(uploadingKey) || submitting}
                        onChange={(e) => handleUpload(index, e)}
                      />
                    </label>
                  </div>
                </div>
              ))
            : null}

          <div className="modal-panel__footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
              關閉
            </button>
            <button type="submit" className="btn btn-success" disabled={submitting || loading || Boolean(uploadingKey)}>
              {submitting ? '提交中…' : '提交'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
